import { fetchApi, trackUserEvent, trackError } from '@vend/utilities'
import { GraphQLErrors } from '@apollo/client/errors'
import { FetchResult } from '@apollo/client'
import { client } from '../apollo'
import { $log } from '../ng-compat/imports'
import {
  CustomFieldMutation,
  SaleUpdateActionInput,
  SetCustomFieldValueInput,
  UpdateSale,
} from '../queries/sale-update'
import { CustomFieldMap } from '../models/custom-fields'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { Sale } from '../models/sale'

/**
 * Post receipt email to api and handle the error.
 *
 * @param sale {Sale}
 *
 * @return {Promise} A promise resolved when receipt email has been sent
 */
export const postEmailReceipt = (
  sale: Readonly<ReduxCurrentSale> | Readonly<Sale>
) => {
  if (sale.receiptID && sale.receiptEmailAddress) {
    return fetchApi(`/api/2.0/email_receipt/${sale.localId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        addresses: [sale.receiptEmailAddress],
        template_id: sale.receiptID,
      }),
    }).catch(err => {
      const moduleName = 'Web Register'
      const eventName = 'Failed to send the reciept by email'
      trackUserEvent(moduleName, eventName, {
        saleId: sale.localId,
        customerId: sale.customer?.id,
        email: sale.receiptEmailAddress,
        templateId: sale.receiptID,
      })
      $log.warn('Failed to send the receipt by email.', err)
      return Promise.resolve()
    })
  }
  return Promise.resolve()
}

type UpdateSaleError = {
  field: string
  message: string
}

type UpdateSaleResponse = {
  saleUpdateActions: {
    success: boolean
    errors?: UpdateSaleError[]
  }
}

const handleFieldErrors = (errors: UpdateSaleError[]) => {
  errors.forEach(error => {
    trackError('Sale Update Error: ', error.message, {
      field: error.field,
      action: 'saleUpdateActions',
    })
  })
}

const handleGraphQLError = (graphQLErrors: GraphQLErrors = []) => {
  graphQLErrors.forEach(error => {
    trackError('Sale Update Error', error.message, {
      action: 'saleUpdateActions',
      graphQLError: error,
    })
  })
}

/**
 * Update sale
 * @return {Promise} A promise resolved once the sale has been posted to the server
 */
export const doUpdateSale = (saleUpdateInput: SaleUpdateActionInput) => {
  return client
    .mutate<UpdateSaleResponse>({
      mutation: UpdateSale,
      variables: {
        input: saleUpdateInput,
      },
    })
    .then(res => {
      res.data?.saleUpdateActions.errors &&
        handleFieldErrors(res.data.saleUpdateActions?.errors)
    })
    .catch(err => handleGraphQLError(err?.graphQLErrors))
}

type SetCustomFieldValuesResponse = {
  name: string
}

export const updateCustomFields = (customFields: CustomFieldMap) => {
  const promises = Object.entries(customFields).reduce(
    (
      acc: Array<Promise<FetchResult<SetCustomFieldValuesResponse>>>,
      [entityID, { values, entity }]
    ) => {
      const entityPromises = values.map(value =>
        client.mutate<SetCustomFieldValuesResponse, SetCustomFieldValueInput>({
          mutation: CustomFieldMutation,
          variables: {
            entityID,
            entity,
            values: value,
          },
        })
      )
      return [...acc, ...entityPromises]
    },
    []
  )
  return promises
}
